class JiscPredictiveSearch extends HTMLElement {
  static cache = new Map();

  static aliases = new Map([
    ['p-3', 'P3'],
    ['p 3', 'P3'],
    ['p-5', 'P5'],
    ['p 5', 'P5'],
    ['r2 v2', 'R2V2'],
    ['r2-v2', 'R2V2'],
    ['800 dx', '800DX'],
    ['snap shell', 'SnapShell'],
    ['scan shell', 'ScanShell'],
    ['acuant', 'GBG'],
    ['gbg', 'Acuant'],
  ]);

  constructor() {
    super();
    this.abortController = null;
    this.debounceTimer = null;
    this.requestSequence = 0;
    this.activeIndex = -1;
    this.currentQuery = '';
    this.isOpen = false;
  }

  connectedCallback() {
    if (this.initialized) return;

    this.form = this.querySelector('form[role="search"]');
    this.input = this.querySelector('[data-jisc-search-input]');
    this.results = this.querySelector('[data-jisc-results]');
    this.status = this.querySelector('[data-jisc-search-status]');

    if (!this.form || !this.input || !this.results || !this.status) return;

    this.initialized = true;
    this.minCharacters = Number(this.dataset.minCharacters) || 2;
    this.debounceDelay = Number(this.dataset.debounce) || 200;
    this.sectionId = this.dataset.sectionId || 'jisc-predictive-search';
    this.predictiveSearchUrl = this.dataset.predictiveSearchUrl;
    this.searchUrl = this.dataset.searchUrl;
    this.loadingText = this.dataset.loadingText || '';
    this.viewAllTemplate = this.dataset.viewAllTemplate || '__QUERY__';
    this.resultsId = this.results.id;

    this.onInput = this.handleInput.bind(this);
    this.onFocus = this.handleFocus.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
    this.onReset = this.handleReset.bind(this);
    this.onFocusout = this.handleFocusout.bind(this);
    this.onPointerdown = this.handlePointerdown.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('focus', this.onFocus);
    this.form.addEventListener('reset', this.onReset);
    this.addEventListener('keydown', this.onKeydown);
    this.addEventListener('focusout', this.onFocusout);
    document.addEventListener('pointerdown', this.onPointerdown);
  }

  disconnectedCallback() {
    window.clearTimeout(this.debounceTimer);
    this.abortController?.abort();
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('focus', this.onFocus);
    this.form?.removeEventListener('reset', this.onReset);
    this.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('focusout', this.onFocusout);
    document.removeEventListener('pointerdown', this.onPointerdown);
  }

  getQuery() {
    return this.input.value.trim().replace(/\s+/g, ' ');
  }

  hasMinimumCharacters(query) {
    return query.replace(/\s/g, '').length >= this.minCharacters;
  }

  handleInput() {
    const query = this.getQuery();
    window.clearTimeout(this.debounceTimer);
    this.clearSelection();
    this.toggleResetButton();

    if (!this.hasMinimumCharacters(query)) {
      this.abortController?.abort();
      this.close();
      return;
    }

    this.debounceTimer = window.setTimeout(() => this.search(query), this.debounceDelay);
  }

  handleFocus() {
    const query = this.getQuery();
    if (!this.hasMinimumCharacters(query)) return;

    if (query === this.currentQuery && this.options.length > 0) {
      this.open();
      return;
    }

    window.clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => this.search(query), this.debounceDelay);
  }

  handleFocusout() {
    window.setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  handleReset(event) {
    event.preventDefault();
    window.clearTimeout(this.debounceTimer);
    this.abortController?.abort();
    this.input.value = '';
    this.toggleResetButton();
    this.close();
    this.input.focus();
  }

  handlePointerdown(event) {
    if (!this.contains(event.target)) this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      if (this.isOpen) {
        event.preventDefault();
        this.close();
      }
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (this.options.length === 0) return;
      event.preventDefault();
      this.open();
      this.moveSelection(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (event.key === 'Enter') {
      if (this.activeIndex >= 0) {
        const selectedOption = this.options[this.activeIndex];
        if (!selectedOption) return;
        event.preventDefault();
        window.location.assign(selectedOption.href);
        return;
      }

      if (event.target === this.input) {
        event.preventDefault();
        this.form.requestSubmit();
      }
    }
  }

  get options() {
    return Array.from(this.results.querySelectorAll('[data-jisc-option]'));
  }

  moveSelection(direction) {
    const options = this.options;
    if (options.length === 0) return;

    this.activeIndex = (this.activeIndex + direction + options.length) % options.length;
    options.forEach((option, index) => {
      option.setAttribute('aria-selected', index === this.activeIndex ? 'true' : 'false');
    });
    this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id);
  }

  clearSelection() {
    this.options.forEach((option) => option.setAttribute('aria-selected', 'false'));
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
  }

  aliasFor(query) {
    return JiscPredictiveSearch.aliases.get(query.toLocaleLowerCase()) || '';
  }

  toggleResetButton() {
    const resetButton = this.form.querySelector('button[type="reset"]');
    if (resetButton) resetButton.classList.toggle('hidden', this.input.value.length === 0);
  }

  cacheKey(query) {
    return `${document.documentElement.lang || 'en'}:${query.toLocaleLowerCase()}`;
  }

  async search(query) {
    const sequence = ++this.requestSequence;
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setLoading(true);

    try {
      const primary = await this.fetchResults(query, this.abortController.signal);
      if (sequence !== this.requestSequence) return;

      let selectedResults = primary;
      const alias = this.aliasFor(query);

      if (alias && primary.productCount === 0) {
        const fallback = await this.fetchResults(alias, this.abortController.signal);
        if (sequence !== this.requestSequence) return;
        if (fallback.totalCount > 0) selectedResults = fallback;
      }

      if (sequence !== this.requestSequence || query !== this.getQuery()) return;
      this.renderResults(selectedResults, query);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.status.textContent = '';
        this.close();
      }
    } finally {
      if (sequence === this.requestSequence) this.setLoading(false);
    }
  }

  async fetchResults(query, signal) {
    const cacheKey = this.cacheKey(query);
    if (JiscPredictiveSearch.cache.has(cacheKey)) return JiscPredictiveSearch.cache.get(cacheKey);

    const url = new URL(this.predictiveSearchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', this.sectionId);
    url.searchParams.set('resources[type]', 'product,page,article,collection');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    const response = await fetch(url, {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) throw new Error(String(response.status));

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const section = documentFragment.querySelector(`#shopify-section-${this.sectionId}`);
    const content = section?.querySelector('[data-jisc-results-content]');

    const result = {
      markup: section?.innerHTML || '',
      totalCount: Number(content?.dataset.resultCount) || 0,
      productCount: Number(content?.dataset.productCount) || 0,
      query,
      statusText: section?.querySelector('[data-jisc-status-text]')?.textContent.trim() || '',
    };

    JiscPredictiveSearch.cache.set(cacheKey, result);
    return result;
  }

  renderResults(result, query) {
    if (!result.markup || result.totalCount === 0) {
      this.results.replaceChildren();
      this.currentQuery = query;
      this.status.textContent = result.statusText || '';
      this.close();
      return;
    }

    this.results.innerHTML = result.markup;
    this.currentQuery = query;
    this.clearSelection();

    this.options.forEach((option, index) => {
      option.id = `${this.resultsId}-option-${index + 1}`;
    });

    const viewAll = this.results.querySelector('[data-jisc-view-all]');
    const viewAllLabel = this.results.querySelector('[data-jisc-view-all-label]');

    if (viewAll) {
      const url = new URL(this.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      viewAll.href = url.toString();
    }

    if (viewAllLabel) {
      viewAllLabel.textContent = this.viewAllTemplate.replace('__QUERY__', query);
    }

    const statusText = this.results.querySelector('[data-jisc-status-text]')?.textContent.trim() || '';
    this.status.textContent =
      result.query && result.query !== query ? statusText.split(result.query).join(query) : statusText;
    this.open();
  }

  setLoading(isLoading) {
    this.toggleAttribute('loading', isLoading);
    this.results.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (isLoading && this.loadingText) this.status.textContent = this.loadingText;
  }

  open() {
    if (this.options.length === 0) return;
    this.results.hidden = false;
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  }

  close() {
    this.results.hidden = true;
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', 'false');
    this.clearSelection();
    this.isOpen = false;
  }
}

if (!customElements.get('jisc-predictive-search')) {
  customElements.define('jisc-predictive-search', JiscPredictiveSearch);
}
